/*
 *
 * p1, p2 and p3, p4 are frustum side and top/bottom planes respectively (or vice-versa,
 * doesn't make a difference). Then, for n = from 1 to 4, we have a plane equation:
 *
 * pnx*x+pny*y+pnz*z+pnw = 0
 *
 * If all points are positioned on the front side of each plane (direction from the plane
 * to the point matches (pnx, pny, pnz)) and the camera is placed at position (x, y, z),
 * the points are fit into the view if the following conditions are met:
 *
 * p1x*x+p1y*y+p1z*z+p1w+1*u = 0
 * p2x*x+p2y*y+p2z*z+p2w+1*u = 0
 * p3x*x+p3y*y+p3z*z+p3w+1*v = 0
 * p4x*x+p4y*y+p4z*z+p4w+1*v = 0
 * v >= 0
 * u >= 0
 *
 * If all four planes intersect at one point, u and v are 0. But that's not necessarily
 * the case, which is why we need these parameters. Then we have to put the camera at such
 * position that a ray casted forward would intersect both line of intersection between p1
 * and p2 and line of intersection between p3 and p4.
 *
 *
 *
 * After some transformations, including subtracting pnw from both sides of each plane
 * equation and turning the equations into matrix-vector multiplication, we get two
 * systems of equations, at least one of which has to be satisfied:
 *
 * { [p1x p1y p1z 0]    [x]      [-p1w]
 * { [p2x p2y p2z 0] \/ [y] ---- [-p2w]
 * { [p3x p3y p3z 1] /\ [z] ---- [-p3w]
 * { [p4x p4y p4z 1]    [v]      [-p4w]
 * {
 * { v >= 0
 *
 * { [p1x p1y p1z 1]    [x]      [-p1w]
 * { [p2x p2y p2z 1] \/ [y] ---- [-p2w]
 * { [p3x p3y p3z 0] /\ [z] ---- [-p3w]
 * { [p4x p4y p4z 0]    [u]      [-p4w]
 * {
 * { u >= 0
 *
 *
 *
 * Solution:
 *
 * [x]      [p1x p1y p1z 0]-1    [-p1w]
 * [y] ---- [p2x p2y p2z 0]   \/ [-p2w]
 * [z] ---- [p3x p3y p3z 1]   /\ [-p3w]
 * [u]      [p4x p4y p4z 1]      [-p4w]
 *
 * if (u < 0) {
 *     [x]      [p1x p1y p1z 1]-1    [-p1w]
 *     [y] ---- [p2x p2y p2z 1]   \/ [-p2w]
 *     [z] ---- [p3x p3y p3z 0]   /\ [-p3w]
 *     [v]      [p4x p4y p4z 0]      [-p4w]
 * }
 *
 */

function makePlaneNormals() {
    return {
        x: [new THREE.Vector3(), new THREE.Vector3()],
        y: [new THREE.Vector3(), new THREE.Vector3()],
    }
}

const PlaneKeys = [
    ["x", 0, "x"],
    ["x", 1, "y"],
    ["y", 0, "z"],
    ["y", 1, "w"],
];

const planeMat = new THREE.Matrix4();
const planeInv = new THREE.Matrix4();
const planeConst = new THREE.Vector4();
const position = new THREE.Vector4();

function fitPointsIntoView(points, planeNormals, target) {
    for (const k of "xyzw") {
        planeConst[k] = -Infinity;
    }

    for (const point of points) {
        for (const [axis, index, k] of PlaneKeys) {
            const normal = planeNormals[axis][index];
            const c = -normal.dot(point);
            planeConst[k] = Math.max(planeConst[k], c);
        }
    }

    const { x: [p1, p2], y: [p3, p4] } = planeNormals;

    planeConst.multiplyScalar(-1);

    planeMat.set(
        p1.x, p1.y, p1.z, 0,
        p2.x, p2.y, p2.z, 0,
        p3.x, p3.y, p3.z, 1,
        p4.x, p4.y, p4.z, 1
    );
    planeInv.getInverse(planeMat);
    position.copy(planeConst).applyMatrix4(planeInv);

    if (position.w < 0) {
        planeMat.elements[12] = 1;
        planeMat.elements[13] = 1;
        planeMat.elements[14] = 0;
        planeMat.elements[15] = 0;
        planeInv.getInverse(planeMat);
        position.copy(planeConst).applyMatrix4(planeInv);
    }

    if (!target) target = new THREE.Vector3();
    return target.set(position.x, position.y, position.z);
}

const tmpBox = new THREE.Box2();

function getCameraPlaneNormals(camera, edgesMultiplier = 1.25, target = makePlaneNormals()) {
    const m = 1/edgesMultiplier;

    tmpBox.min.set(-m, -m);
    tmpBox.max.set( m,  m);

    return getCameraBoxPlaneNormals(camera, tmpBox, target);
}

function getCameraBoxPlaneNormals(camera, box, target = makePlaneNormals()) {
    const vDir = Math.tan(THREE.Math.degToRad(camera.fov*0.5))/camera.zoom;
    const hDir = vDir*camera.aspect;

    target.x[0].set( 1, 0,  hDir*box.min.x);
    target.x[1].set(-1, 0, -hDir*box.max.x);
    target.y[0].set(0,  1,  vDir*box.min.y);
    target.y[1].set(0, -1, -vDir*box.max.y);

    for (const [axis, index, k] of PlaneKeys) {
        target[axis][index].normalize().applyQuaternion(camera.quaternion);
    }

    return target;
}

const camera = new THREE.PerspectiveCamera();
const projMat = new THREE.Matrix4();

function getPointsOnScreenBoundingBox(cameraData, points, target = new THREE.Box2()) {
    const { position, quaternion, fov, zoom, aspect } = cameraData;

    camera.position.copy(position);
    camera.quaternion.copy(quaternion);
    camera.fov = fov;
    camera.zoom = zoom;
    camera.aspect = aspect;

    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();
    projMat.copy(camera.projectionMatrix).multiply(camera.matrixWorldInverse);

    target.makeEmpty();

    for (const point of points) {
        point.applyMatrix4(projMat);

        if (isFinite(point.x)) target.min.x = Math.min(target.min.x, point.x);
        if (isFinite(point.x)) target.max.x = Math.max(target.max.x, point.x);
        if (isFinite(point.y)) target.min.y = Math.min(target.min.y, point.y);
        if (isFinite(point.y)) target.max.y = Math.max(target.max.y, point.y);
    }

    target.min.x = Math.max(target.min.x, -1);
    target.max.x = Math.min(target.max.x,  1);
    target.min.y = Math.max(target.min.y, -1);
    target.max.y = Math.min(target.max.y,  1);

    return target;
}

export {
    getCameraPlaneNormals,
    fitPointsIntoView,
    makePlaneNormals,
    getPointsOnScreenBoundingBox,
    getCameraBoxPlaneNormals,
};
